/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BaseButton from "components/common/Button/Base";
import BaseInput from "components/common/Input/Base";
import {
  BusinessTakeover,
  Journal,
  JournalNewzenResult,
  ResJournalNewzen
} from "models/journal.model";
import {
  getBusinessTakeover,
  getNewzenBusinessList,
  sendJournalsToNewzen
} from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { convertCurrency } from "utils/converter";
import {
  isBalanced,
  journalStatus,
  memoText,
  SLIP_TYPE_LABELS,
  toDigits
} from "pages/Journal/journal.logic";

interface NewzenBusiness {
  code: string;
  name: string;
  period: string;
  regNo: string;
  startDate: string; // yyyyMMdd
  endDate: string; // yyyyMMdd
}

type PlanKind = "send" | "skip" | "exclude";

interface PlanItem {
  journal: Journal;
  kind: PlanKind;
  reason?: string; // 건너뜀·제외 사유
  warning?: string; // 보내지만 알릴 것 (회사 1개일 때 기수 밖)
}

const PERIOD_OUT_MESSAGE = "전표 일자가 세무사랑 회사 기수 밖입니다.";
const TAKEOVER_MESSAGE = "인수일 이전 일자의 전표는 등록·전송할 수 없습니다.";

const SKIP_REASON_LABELS: Record<string, string> = {
  already_sent: "이미 전송",
  held: "보류",
  confirmed: "마감",
  not_found: "전표 없음",
  sending: "다른 요청이 전송 중"
};

const coversPeriod = (company: NewzenBusiness, journal: Journal) => {
  const date = toDigits(journal.makeDt);
  return date >= company.startDate && date <= company.endDate;
};

// 회사가 1개면 그 회사, 여럿이면 선택한 전표 일자를 모두 덮는 기수의 회사
const pickDefaultCompany = (
  companies: NewzenBusiness[],
  journals: Journal[]
): string | null => {
  if (companies.length === 1) return companies[0].code;
  const covering = companies.find((company) =>
    journals.every((journal) => coversPeriod(company, journal))
  );
  return covering?.code || null;
};

const buildPlan = (
  journals: Journal[],
  companies: NewzenBusiness[],
  company: NewzenBusiness | undefined,
  takeover: BusinessTakeover | null
): PlanItem[] =>
  journals.map((journal): PlanItem => {
    const status = journalStatus(journal);
    if (status === "sent")
      return { journal, kind: "skip", reason: "이미 전송" };
    if (status === "held") return { journal, kind: "skip", reason: "보류" };
    if (journal.isAccountingConfirmed)
      return { journal, kind: "skip", reason: "마감" };

    if (!isBalanced(journal))
      return { journal, kind: "exclude", reason: "대차 불일치" };
    if (takeover?.date && toDigits(journal.makeDt) < toDigits(takeover.date))
      return { journal, kind: "exclude", reason: TAKEOVER_MESSAGE };
    if (company && !coversPeriod(company, journal)) {
      // 세무사랑은 지금 열린 기수만 알려 주므로, 회사가 1개면 서버처럼 경고만 하고 보낸다
      if (companies.length > 1)
        return { journal, kind: "exclude", reason: PERIOD_OUT_MESSAGE };
      return { journal, kind: "send", warning: PERIOD_OUT_MESSAGE };
    }
    return { journal, kind: "send" };
  });

const journalTitle = (journal: Journal) =>
  `${journal.makeDt} ${SLIP_TYPE_LABELS[journal.slipType]} ${
    memoText(journal) || ""
  }`.trim();

interface NewzenSendDialogProps {
  isOpen: boolean;
  bizId: string | undefined;
  journals: Journal[]; // 선택한 전표 전체 (건너뜀 집계 포함)
  onClose: () => void;
  onFinished: () => void; // 전송을 한 번이라도 했으면 닫을 때 목록 새로고침
}

function NewzenSendDialog({
  isOpen,
  bizId,
  journals,
  onClose,
  onFinished
}: NewzenSendDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [companies, setCompanies] = useState<NewzenBusiness[]>([]);
  const [takeover, setTakeover] = useState<BusinessTakeover | null>(null);
  const [checkedBizCode, setCheckedBizCode] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [result, setResult] = useState<ResJournalNewzen | null>(null);

  useEffect(() => {
    if (!isOpen || !bizId) return;

    setCompanies([]);
    setTakeover(null);
    setCheckedBizCode(null);
    setLoadError("");
    setSendError("");
    setResult(null);
    setIsLoading(true);

    let cancelled = false;
    Promise.all([
      getNewzenBusinessList(bizId),
      // 인수일 조회 실패는 전송을 막지 않는다 (서버가 takeover_locked로 다시 막음)
      getBusinessTakeover(bizId).catch(() => null)
    ])
      .then(([newzenRes, takeoverRes]) => {
        if (cancelled) return;
        const list: NewzenBusiness[] = newzenRes.data || [];
        setCompanies(list);
        setTakeover(takeoverRes?.data || null);
        setCheckedBizCode(pickDefaultCompany(list, journals));
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setIsLoading(false);
        setLoadError(
          err.response?.data?.message ||
            "세무사랑에 등록된 사업자 정보를 받아오는 중 오류가 발생했습니다."
        );
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bizId]);

  const company = companies.find((c) => c.code === checkedBizCode);
  const plan = useMemo(
    () => buildPlan(journals, companies, company, takeover),
    [journals, companies, company, takeover]
  );
  const sendItems = plan.filter((item) => item.kind === "send");
  const skipItems = plan.filter((item) => item.kind === "skip");
  const checkItems = plan.filter(
    (item) => item.kind === "exclude" || item.warning
  );
  const amountOf = (items: PlanItem[]) =>
    items.reduce((acc, item) => acc + item.journal.totalDebit, 0);

  const journalById = useMemo(
    () => new Map(journals.map((journal) => [journal.id, journal])),
    [journals]
  );

  const send = () => {
    if (!bizId || !checkedBizCode || !sendItems.length || isSending) return;
    setIsSending(true);
    setSendError("");
    sendJournalsToNewzen(bizId, {
      bizCode: checkedBizCode,
      ids: sendItems.map((item) => item.journal.id)
    })
      .then((res) => {
        setIsSending(false);
        setResult({
          results: res.data?.results || [],
          warnings: res.data?.warnings || []
        });
      })
      .catch((err) => {
        // E004: 회사코드 없음·세무사랑 접속 불가 등 전송 전체가 못 돈 경우
        setIsSending(false);
        setSendError(
          err.response?.data?.message || "전송 중 오류가 발생했습니다."
        );
      });
  };

  const close = () => {
    if (isSending) return;
    if (result) onFinished();
    onClose();
  };

  const notSentCount =
    result?.results.filter((r) => r.result !== "sent").length || 0;

  return (
    <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle
        css={css`
          background-color: ${PALETTE.background};
        `}
      >
        일반전표 세무사랑 전송
      </DialogTitle>
      <div
        css={css`
          height: 4px;
        `}
      >
        {(isLoading || isSending) && (
          <LinearProgress
            variant="indeterminate"
            sx={{
              backgroundColor: PALETTE.primaryBackground,
              "& .MuiLinearProgress-bar": { backgroundColor: PALETTE.primary }
            }}
          />
        )}
      </div>
      <DialogContent>
        <div
          css={css`
            min-width: 400px;
            display: flex;
            flex-direction: column;
            gap: 16px;
            font-size: 14px;
          `}
        >
          {result ? (
            <ResultView
              result={result}
              journalById={journalById}
              notSentCount={notSentCount}
            />
          ) : (
            <>
              <Section title="세무사랑 회사">
                {loadError ? (
                  <ErrorText>{loadError}</ErrorText>
                ) : companies.length ? (
                  companies.map((item) => (
                    <label
                      key={item.code}
                      htmlFor={`journal-newzen-${item.code}`}
                      css={css`
                        padding: 16px;
                        border: 1px solid
                          ${
                            checkedBizCode === item.code
                              ? PALETTE.primary
                              : PALETTE.border
                          };
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        cursor: pointer;

                        &:hover {
                          margin: -1px;
                          border-width: 2px;
                          border-color: ${PALETTE.primary};
                        }
                      `}
                    >
                      <BaseInput
                        id={`journal-newzen-${item.code}`}
                        type="radio"
                        name="journal-newzen-company"
                        value={item.code}
                        checked={checkedBizCode === item.code}
                        disabled={isSending}
                        onChange={(e) => setCheckedBizCode(e.target.value)}
                        css={css`
                          &:checked {
                            accent-color: ${PALETTE.primary};
                          }
                        `}
                      />
                      <div
                        css={css`
                          flex: 1;
                          display: flex;
                          justify-content: space-between;
                          gap: 8px;
                        `}
                      >
                        <span
                          css={css`
                            font-weight: 600;
                          `}
                        >
                          {item.name} ({item.code})
                        </span>
                        <span
                          css={css`
                            color: ${PALETTE.textMid};
                          `}
                        >
                          {fmtYmd(item.startDate)} ~ {fmtYmd(item.endDate)}
                        </span>
                      </div>
                    </label>
                  ))
                ) : (
                  <div
                    css={css`
                      color: ${PALETTE.textMid};
                    `}
                  >
                    {isLoading
                      ? "선택 가능한 사업자를 받아오는 중입니다."
                      : "선택 가능한 사업자가 없습니다."}
                  </div>
                )}
              </Section>

              <Section title="처리 요약">
                <div>
                  새로 전송 <strong>{sendItems.length}건</strong> (
                  {convertCurrency(amountOf(sendItems))}원) · 건너뜀{" "}
                  <strong>{skipItems.length}건</strong> (
                  {convertCurrency(amountOf(skipItems))}원)
                </div>
                {skipItems.length > 0 && (
                  <div
                    css={css`
                      color: ${PALETTE.textMid};
                      font-size: 13px;
                    `}
                  >
                    {countByReason(skipItems)}
                  </div>
                )}
              </Section>

              {checkItems.length > 0 && (
                <Section title="전송 전 점검">
                  <ItemList>
                    {checkItems.map((item) => (
                      <ItemRow
                        key={item.journal.id}
                        title={journalTitle(item.journal)}
                        amount={item.journal.totalDebit}
                        note={
                          item.kind === "exclude"
                            ? `제외 — ${item.reason}`
                            : `경고(전송함) — ${item.warning}`
                        }
                        noteColor={
                          item.kind === "exclude"
                            ? PALETTE.error
                            : PALETTE.textMid
                        }
                      />
                    ))}
                  </ItemList>
                </Section>
              )}

              {sendError && <ErrorText>{sendError}</ErrorText>}
            </>
          )}
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px 24px;
        `}
      >
        {result ? (
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            onClick={close}
          >
            닫기
          </BaseButton>
        ) : (
          <>
            <BaseButton
              variant="outlined"
              color={PALETTE.primary}
              disabled={isSending}
              onClick={close}
            >
              닫기
            </BaseButton>
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              disabled={
                isLoading || isSending || !checkedBizCode || !sendItems.length
              }
              onClick={send}
              css={css`
                min-width: 90px;
              `}
            >
              {isSending ? "전송 중" : `${sendItems.length}건 전송`}
            </BaseButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ResultView({
  result,
  journalById,
  notSentCount
}: {
  result: ResJournalNewzen;
  journalById: Map<string, Journal>;
  notSentCount: number;
}) {
  const sentCount = result.results.length - notSentCount;
  return (
    <>
      <div>
        전송 완료 <strong>{sentCount}건</strong>
        {notSentCount > 0 && (
          <span
            css={css`
              color: ${PALETTE.error};
            `}
          >
            {" "}
            · {result.results.length}건 중 {notSentCount}건이 전송되지
            않았습니다. 실패 사유를 확인해 주세요.
          </span>
        )}
      </div>
      {result.warnings.length > 0 && (
        <div
          css={css`
            padding: 10px 14px;
            border-radius: 8px;
            background-color: ${PALETTE.backgroundDisabled};
            color: ${PALETTE.text};
            font-size: 13px;
            white-space: pre-line;
          `}
        >
          {result.warnings.join("\n")}
        </div>
      )}
      <ItemList>
        {result.results.map((r) => {
          const journal = journalById.get(r.id);
          return (
            <ItemRow
              key={r.id}
              title={journal ? journalTitle(journal) : r.id}
              amount={journal?.totalDebit}
              note={resultText(r)}
              noteColor={
                r.result === "sent"
                  ? PALETTE.progressCompleted
                  : r.result === "failed"
                    ? PALETTE.error
                    : PALETTE.textMid
              }
              icon={
                r.result === "sent" ? (
                  <CheckCircleIcon
                    fontSize="small"
                    css={css`
                      color: ${PALETTE.progressCompleted};
                    `}
                  />
                ) : r.result === "failed" ? (
                  <ErrorIcon
                    fontSize="small"
                    css={css`
                      color: ${PALETTE.error};
                    `}
                  />
                ) : undefined
              }
            />
          );
        })}
      </ItemList>
    </>
  );
}

const resultText = (r: JournalNewzenResult) => {
  switch (r.result) {
    case "sent": {
      const sent = r.newzenSlipNo
        ? `전송 완료 #${r.newzenSlipNo}`
        : "전송 완료";
      // 세무사랑에는 들어갔으니 다시 보내면 안 된다 (journal-api.md 3.5)
      return r.message
        ? `${sent} — 기록 저장 실패(${r.message}). 다시 보내지 말고 관리자에게 알려 주세요.`
        : sent;
    }
    case "skipped":
      return `건너뜀(${
        (r.reason && SKIP_REASON_LABELS[r.reason]) || r.reason || "-"
      })`;
    default:
      return `전송 실패(${r.message || r.rule || "-"})`;
  }
};

const countByReason = (items: PlanItem[]) => {
  const counts = new Map<string, number>();
  items.forEach((item) => {
    const reason = item.reason || "";
    counts.set(reason, (counts.get(reason) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([reason, count]) => `${reason} ${count}건`)
    .join(" · ");
};

const fmtYmd = (value: string) =>
  value && value.length >= 8
    ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
    : value;

function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      <div
        css={css`
          font-weight: 600;
          color: ${PALETTE.text};
        `}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        color: ${PALETTE.error};
        white-space: pre-line;
      `}
    >
      {children}
    </div>
  );
}

function ItemList({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        max-height: 280px;
        overflow-y: auto;
        border: 1px solid ${PALETTE.border};
        border-radius: 8px;
      `}
    >
      {children}
    </div>
  );
}

function ItemRow({
  title,
  amount,
  note,
  noteColor,
  icon
}: {
  title: string;
  amount?: number;
  note: string;
  noteColor: string;
  icon?: React.ReactNode;
}) {
  return (
    <div
      css={css`
        padding: 10px 14px;
        display: flex;
        align-items: flex-start;
        gap: 10px;
        border-bottom: 1px solid ${PALETTE.borderLight};

        &:last-child {
          border-bottom: none;
        }
      `}
    >
      <div
        css={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            gap: 8px;
            font-size: 13px;
            color: ${PALETTE.text};
          `}
        >
          <span
            css={css`
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {title}
          </span>
          {amount !== undefined && (
            <span
              css={css`
                flex-shrink: 0;
              `}
            >
              {convertCurrency(amount)}원
            </span>
          )}
        </div>
        <div
          css={css`
            font-size: 12px;
            color: ${noteColor};
          `}
        >
          {note}
        </div>
      </div>
      {icon}
    </div>
  );
}

export default NewzenSendDialog;
